import * as THREE from "three";
import { applyVariantCompositeTransform, resolveCollisionSpec, applyMirror, collisionBoxesUnion } from "../utils/geometry.js";

// Constants
const GHOST_OPACITY = 0.3;
const GHOST_RENDER_ORDER = 1000;
const GHOST_COLOR_DEFAULT = 0x4facfe; // Blue
const GHOST_COLOR_SNAPPED = 0x00ff00; // Green
const GHOST_COLOR_INVALID = 0xff3333; // Red – snapped but out of bounds

// Scratch Euler for reading the mesh's Y rotation without per-call allocation.
const _scratchEuler = new THREE.Euler();
const _Y_AXIS = new THREE.Vector3(0, 1, 0);

export class GhostFurniture {
  constructor(furnitureData, originalMesh, { sourceIsLive = false } = {}) {
    this.furnitureData = furnitureData;
    this.originalMesh = originalMesh;
    // When the source is a live FurnitureElement mesh (drag-relocate), the current
    // pose is already baked in — mirror scaling has been applied, and storage/variant
    // transforms reflect runtime state. Skip rest-pose-only setup in that case.
    this.sourceIsLive = sourceIsLive;
    this.mesh = null;
    this.isVisible = false;
    this.currentRotation = 0; // Track rotation in radians
    this.snappedAttachmentPoint = null; // Track snapped attachment point
    this.isSnapped = false; // Track snap state
    this.isPlaceable = true; // False when out-of-bounds or colliding (blocks placement)

    this.init();
  }

  init() {
    if (this.originalMesh) {
      this.createGhostMesh();
    }
  }

  createGhostMesh() {
    // Object3D.clone() deep-copies userData via JSON.stringify, which throws on
    // circular structures. The live mesh has userData.furnitureElement → FurnitureElement
    // → mesh back-reference, so we temporarily detach those refs across the whole tree,
    // clone, then restore on the source.
    const detachedRefs = [];
    if (this.sourceIsLive) {
      this.originalMesh.traverse((node) => {
        if (node.userData && node.userData.furnitureElement !== undefined) {
          detachedRefs.push([node, node.userData.furnitureElement]);
          node.userData.furnitureElement = undefined;
        }
      });
    }
    try {
      this.mesh = this.originalMesh.clone();
    } finally {
      for (const [node, ref] of detachedRefs) {
        node.userData.furnitureElement = ref;
      }
    }

    this.mesh.traverse((child) => {
      if (child.isMesh) {
        child.userData.isGhost = true;

        if (Array.isArray(child.material)) {
          child.material = child.material.map(() => this.createGhostMaterial());
        } else {
          child.material = this.createGhostMaterial();
        }

        // Ghosts don't cast shadows by default. The drag ghost opts in via
        // setCastShadow(true) so the moving furniture keeps a grounding shadow
        // while its real mesh is hidden — but the catalog-spawn ghost stays
        // shadowless (otherwise it leaves a stale shadow after placement).
        child.castShadow = false;
        child.receiveShadow = false;
        child.renderOrder = GHOST_RENDER_ORDER;
      }
    });

    this.mesh.visible = false;

    // Mirror, preserving the object's center (see applyMirror). Skip when cloning
    // from a live mesh — the flip is already baked into the source clone.
    if (!this.sourceIsLive && this.furnitureData?.properties?.mirror) {
      applyMirror(this.mesh, this.furnitureData.properties.mirror);
    }

    // For catalog-spawn ghosts (rest pose), the cached GLTF source has the embedded
    // composite group at its authored rest location, not at variant 0's offset.
    // Apply variant 0's compositeOffset/RotationY to match what a placed FurnitureElement
    // would show. Skipped for live-mesh clones (current variant transform already baked in).
    if (!this.sourceIsLive) {
      applyVariantCompositeTransform(this.mesh, this.furnitureData, 0);
    }
  }

  createGhostMaterial() {
    return new THREE.MeshBasicMaterial({
      color: GHOST_COLOR_DEFAULT,
      transparent: true,
      opacity: GHOST_OPACITY,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
    });
  }

  setPosition(x, y, z) {
    if (this.mesh) {
      this.mesh.position.set(x, y, z);
    }
  }

  setRotation(angleY) {
    if (this.mesh) {
      // Use quaternion to avoid corrupting X/Z euler components on cloned live meshes
      this.mesh.quaternion.setFromAxisAngle(_Y_AXIS, angleY);
      this.currentRotation = angleY;
    }
  }

  getCurrentRotation() {
    return this.currentRotation;
  }

  /**
   * Mesh-local-frame collision boxes for precise multi-box overlap tests.
   * Ghosts always preview at variant 0 (user switches variants after spawning).
   * Returns null when only a single authored box (or none) is present — caller
   * falls back to the union AABB from getLocalSize() / getCollisionCenterOffset().
   * @returns {Array<{size:{x,y,z}, offset:{x,y,z}}>|null}
   */
  getCollisionBoxes() {
    const spec = resolveCollisionSpec(this.furnitureData?.properties, 0);
    if (!spec?.boxes?.length) return null;
    return spec.boxes.map(cb => ({
      size:   { x: cb.size.x, y: cb.size.y, z: cb.size.z },
      offset: { x: cb.offset?.x ?? 0, y: cb.offset?.y ?? 0, z: cb.offset?.z ?? 0 },
    }));
  }

  /**
   * Compute the ghost's collision-volume size at the current rotation.
   *
   * Source priority:
   *   1. authored variants[0].collisionBox(es) / properties.collisionBox(es)
   *      — preferred so the ghost reports the same footprint a placed
   *      FurnitureElement would.
   *   2. raw mesh AABB via teleport-to-origin (Box3.setFromObject).
   *
   * Result is cached and invalidated when rotation changes.
   * @returns {THREE.Vector3} World-axis-aligned size at current rotation
   */
  getLocalSize() {
    if (!this.mesh) return new THREE.Vector3();

    // Sync currentRotation from the mesh quaternion. EventManager sets the ghost's
    // quaternion directly (parent's world quat * componentRotationY) without going
    // through setRotation(), so currentRotation can otherwise be stale and produce
    // a wrong-axis ghost AABB on rotated parents.
    this.currentRotation = _scratchEuler.setFromQuaternion(this.mesh.quaternion, "YXZ").y;

    if (this._cachedLocalSize && this._cachedLocalSizeRotation === this.currentRotation) {
      return this._cachedLocalSize;
    }

    const spec = resolveCollisionSpec(this.furnitureData?.properties, 0);
    let size;

    if (spec) {
      const ext = this._computeAuthoredExtents(spec);
      size = new THREE.Vector3(ext.halfWidth * 2, ext.height, ext.halfDepth * 2);
      this._cachedCenterOffsetX = ext.centerOffsetX;
      this._cachedCenterOffsetY = ext.centerOffsetY;
      this._cachedCenterOffsetZ = ext.centerOffsetZ;
    } else {
      // Fallback: raw mesh AABB (teleport to origin keeping rotation)
      const savedPosition = this.mesh.position.clone();
      this.mesh.position.set(0, 0, 0);
      this.mesh.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(this.mesh);
      this.mesh.position.copy(savedPosition);
      this.mesh.updateMatrixWorld(true);
      size = box.isEmpty() ? new THREE.Vector3() : box.getSize(new THREE.Vector3());
      this._cachedCenterOffsetX = 0;
      this._cachedCenterOffsetY = box.isEmpty() ? 0 : (box.min.y + box.max.y) / 2;
      this._cachedCenterOffsetZ = 0;
    }

    this._cachedLocalSize = size;
    this._cachedLocalSizeRotation = this.currentRotation;
    return size;
  }

  /** Mesh-local collision box, same shape and frame as FurnitureElement.boundingBox. */
  getLocalCollisionBox() {
    if (!this.mesh) return null;
    if (this._localCollisionBox) return this._localCollisionBox;

    const spec = resolveCollisionSpec(this.furnitureData?.properties, 0);
    let center, size;
    if (spec) {
      const ext = this._computeAuthoredExtents(spec, 0);
      center = new THREE.Vector3(ext.centerOffsetX, ext.centerOffsetY, ext.centerOffsetZ);
      size = new THREE.Vector3(ext.halfWidth * 2, ext.height, ext.halfDepth * 2);
    } else {
      this.mesh.updateWorldMatrix(true, true);
      const invWorld = this.mesh.matrixWorld.clone().invert();
      const toLocal = new THREE.Matrix4();
      const box = new THREE.Box3();
      const childBox = new THREE.Box3();
      this.mesh.traverse((child) => {
        if (!child.isMesh || !child.geometry) return;
        if (!child.geometry.boundingBox) child.geometry.computeBoundingBox();
        toLocal.multiplyMatrices(invWorld, child.matrixWorld);
        box.union(childBox.copy(child.geometry.boundingBox).applyMatrix4(toLocal));
      });
      if (box.isEmpty()) return null;
      center = box.getCenter(new THREE.Vector3());
      size = box.getSize(new THREE.Vector3());
    }
    this._localCollisionBox = { localCenterOffset: center, size };
    return this._localCollisionBox;
  }

  /**
   * Compute world-axis-aligned extents and center offset (relative to mesh.position)
   * for the authored collision spec at the current rotation.
   */
  _computeAuthoredExtents(spec, rotation = this.currentRotation) {
    const { minX, maxX, minY, maxY, minZ, maxZ } = collisionBoxesUnion(spec.boxes ?? [spec.box], rotation);
    return {
      halfWidth: (maxX - minX) / 2,
      halfDepth: (maxZ - minZ) / 2,
      height: maxY - minY,
      centerOffsetX: (minX + maxX) / 2,
      centerOffsetY: (minY + maxY) / 2,
      centerOffsetZ: (minZ + maxZ) / 2,
    };
  }

  /**
   * World-axis-aligned offset from `mesh.position` to the collision-volume center
   * at the current rotation. Non-zero when authored boxes have a translation offset
   * (e.g. bonbon3-f closed has -0.16 along Z).
   * @returns {{x: number, y: number, z: number}}
   */
  getCollisionCenterOffset() {
    // Ensure cache is populated for current rotation
    this.getLocalSize();
    return {
      x: this._cachedCenterOffsetX ?? 0,
      y: this._cachedCenterOffsetY ?? 0,
      z: this._cachedCenterOffsetZ ?? 0,
    };
  }

  setSnapped(snapped, attachmentPoint = null, childAttachmentSide = null, isValid = true) {
    this.isSnapped = snapped;
    this.snappedAttachmentPoint = attachmentPoint;
    this.childAttachmentSide = childAttachmentSide;  // Store which child attachment was used
    this.isPlaceable = isValid; // Block placement when out-of-bounds or colliding

    // Change color based on snap state and validity. An invalid ghost usually sits inside
    // the piece it overlaps, so it draws over everything rather than vanishing into it.
    if (this.mesh) {
      const color = snapped
        ? (isValid ? GHOST_COLOR_SNAPPED : GHOST_COLOR_INVALID)
        : (isValid ? GHOST_COLOR_DEFAULT : GHOST_COLOR_INVALID);
      this.mesh.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        for (const mat of Array.isArray(child.material) ? child.material : [child.material]) {
          mat.color?.setHex(color);
          mat.depthTest = isValid;
        }
      });
    }
  }

  getSnappedAttachmentPoint() {
    return this.snappedAttachmentPoint;
  }

  getChildAttachmentSide() {
    return this.childAttachmentSide;
  }

  show() {
    if (this.mesh) {
      this.mesh.visible = true;
      this.isVisible = true;
    }
  }

  hide() {
    if (this.mesh) {
      this.mesh.visible = false;
      this.isVisible = false;
      this.setSnapped(false, null); // Reset snap state when hiding
    }
  }

  // Opt the ghost into casting shadows (used by the drag ghost so the moving
  // furniture keeps a grounding shadow while its real mesh is hidden). Shadow
  // casting runs through Three's depth pass, so the transparent preview material
  // still grounds correctly.
  setCastShadow(enabled) {
    if (!this.mesh) return;
    this.mesh.traverse((child) => {
      if (child.isMesh) child.castShadow = enabled;
    });
  }

  getMesh() {
    return this.mesh;
  }

  // The materials are the ghost's own; the geometry is borrowed from the source it was cloned
  // from (a placed piece, or the cached model every placed copy shares), so it stays.
  dispose() {
    this.mesh?.traverse((child) => {
      if (!child.material) return;
      for (const mat of Array.isArray(child.material) ? child.material : [child.material]) mat.dispose();
    });
  }
}